import React, { useMemo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import ChartCard from "../ChartCard";
import { getPieComparisons, isLessThanOne } from "../../../utils/helpers";
import TEMPORAL_CHART_PALETTE from "./temporalChartPalette";
import ReusableBarChart from "./BarChart";
import TemporalGenderChart from "./TemporalGenderChart";

// Helper to normalize raw data
function normalizeData(rawData) {
    if (Array.isArray(rawData[0])) {
        const [org = [], peers = [], clients = []] = rawData;
        return org.map((d, i) => ({
            // groupLabel: d.label ?? d.gender,
            groupLabel: d.gender,
            Org: d.value,
            Industrypeers: peers[i]?.value ?? 0,
            unumclient: clients[i]?.value ?? 0,
            count: d.count,
        }));
    }
    return rawData;
}

const CustomTooltip = ({ count }) => {
    return (
        <div className="dashboard-tooltip">
            <label>Total count: {count}</label>
            <div className="tooltip-Arrow"></div>
        </div>
    );
};

CustomTooltip.propTypes = {
    count: PropTypes.number,
};

// Helper to check if data has valid numbers
function hasValidNumbers(data) {
    return (
        Array.isArray(data) &&
        data.some(
            (item) =>
                item &&
                typeof item.groupLabel === "string" &&
                [item.Org, item.Industrypeers, item.unumclient].some(
                    (v) => typeof v === "number" && !isNaN(v)
                )
        )
    );
}

// Helper to get display label
const displayLabelMap = {
    Org: (orgName) => orgName,
    IndustryPeers: () => "Industry Peers",
    unumclient: () => "Unum Clients",
};

const getCompText = (selected) => {
    if (selected?.length === 2) return "All";
    if (selected?.length === 1) return selected[0]?.value;
    return "Organization Only";
};

const getChartData = (data, key) =>
    data.map((d) => {
        const matchingKey = Object.keys(d).find(
            (k) => k.toLowerCase() === key.toLowerCase()
        );
        return {
            id: d.groupLabel,
            value: d[matchingKey] ?? 0,
            count: d.count,
        };
    });

// Display values in the range (0, 1) as "<1"; show 0 as "0"
const formatPieDisplayValue = (value) => (isLessThanOne(value) ? "<1" : value);

const createDashboardTooltip =
    (label) =>
    ({ datum }) =>
        datum.id == null ? null : (
            <div className="dashboard-tooltip">
                {label}: {datum.id} {formatPieDisplayValue(datum.value ?? 0)}%
            </div>
        );

// ArcLinkLabel for dashboard
const DashboardArcLinkLabel = ({ id, value }) => (
    <>
        <tspan x="0" dy="0" className="charts-inner-text">
            {id}
        </tspan>
        <tspan
            x={id === "Female" ? "0.8em" : "0"}
            dy="1.2em"
            className="charts-inner-text"
        >
            {formatPieDisplayValue(value)}%
        </tspan>
    </>
);

DashboardArcLinkLabel.propTypes = {
    id: PropTypes.string,
    value: PropTypes.number,
};

// ArcLinkLabel for disability snapshot
const DisabilityArcLinkLabel = ({ id, value }) => (
    <>
        <tspan x={"0em"} dy={"0em"} className="charts-inner-text">
            {formatPieDisplayValue(value)}%
        </tspan>
    </>
);

DisabilityArcLinkLabel.propTypes = {
    id: PropTypes.string,
    value: PropTypes.number,
};

// Extracted inline functions — NO logic removed

const dashboardArcLinkLabelFn = (d) => (
    <DashboardArcLinkLabel id={d.id} value={d.value} />
);

const disabilityArcLinkLabelFn = (d) => (
    <DisabilityArcLinkLabel id={d.id} value={d.value} />
);

const disabilityTooltipFn = (d) => <CustomTooltip count={d.datum.data.count} />;

const createSymbolShapeFn = (comparisons) => {
    const SymbolShape = ({ y, size, fill, id }) => {
        const stroke =
            comparisons.find((c) => c.key === id)?.borderColor || "#015294";

        return (
            <rect
                x={2}
                y={y}
                width={size}
                height={size}
                rx={3}
                ry={3}
                fill={fill}
                stroke={stroke}
                strokeWidth={1}
            />
        );
    };

    SymbolShape.propTypes = {
        y: PropTypes.number,
        size: PropTypes.number,
        fill: PropTypes.string,
        id: PropTypes.string,
    };

    return SymbolShape;
};

const ReusableDonutChart = ({
    chartId,
    title,
    description,
    accessibilityText,
    data: rawData = [],
    organizationName,
    selectedCompanyYourCompanyTo,
    selectedFilters,
    groupBy,
    layout,
    disabilitySnapshot,
    errorMessage,
    totalRecords,
    viewId,
    tableData,
    reportDetails,
    disclaimer,
    cacheKey,
    clientClaimCount,
}) => {
    const [showPeriodBars, setShowPeriodBars] = useState(false);
    const isTemporalChart = groupBy === "year" || groupBy === "quarter";
    const canToggleGrouping = !disabilitySnapshot && isTemporalChart;
    const data = useMemo(
        () => (isTemporalChart ? rawData : normalizeData(rawData)),
        [rawData, isTemporalChart]
    );

    const compText = useMemo(
        () => getCompText(selectedCompanyYourCompanyTo),
        [selectedCompanyYourCompanyTo]
    );

    const comparisonMap = useMemo(
        () => getPieComparisons?.(organizationName) ?? {},
        [organizationName]
    );

    const comparisons = comparisonMap[compText] || [];

    const keysToUse =
        comparisons.length > 0 ? comparisons.map((c) => c.key) : ["Org"];

    const buildPieOption = useCallback(
        (key, baseConfig, cmp, chartData) => ({
            ...baseConfig,
            data: chartData,
            colors: cmp.pieColors,
            borderColor: cmp.borderColor || baseConfig.borderColor,
        }),
        []
    );

    // ------------------------
    // Dashboard Pie Options
    // ------------------------
    const dashboardPieOptions = useMemo(() => {
        if (isTemporalChart) {
            const comparison = {
                ...(comparisons[0] || {}),
                pieColors: { datum: "data.color" },
                borderColor: "#FFFFFF",
            };
            return data.map((period) => {
                const chartData = (period.values || []).map((value, index) => ({
                    id: value.gender,
                    value: value.value ?? 0,
                    count: value.count,
                    color: TEMPORAL_CHART_PALETTE[
                        index % TEMPORAL_CHART_PALETTE.length
                    ].color,
                }));
                const base = {
                    margin: { top: 50, right: 110, bottom: 50, left: 110 },
                    innerRadius: 0.75,
                    padAngle: 2.5,
                    cornerRadius: 3,
                    borderWidth: 1,
                    borderColor: comparison.borderColor,
                    enableArcLabels: false,
                    isInteractive: true,
                    tooltip: createDashboardTooltip(period.temporalLabel),
                    arcLinkLabel: ({ label, value }) =>
                        `${label} ${formatPieDisplayValue(value)}%`,
                    enableArcLinkLabels: true,
                    arcLinkLabelsSkipAngle: 0,
                    arcLinkLabelsTextColor: "#525252",
                    arcLinkLabelsThickness: 1,
                    arcLinkLabelsColor: "#015294",
                    arcLinkLabelsOffset: 0,
                    arcLinkLabelsDiagonalLength: 15,
                    arcLinkLabelsStraightLength: 0,
                    animate: true,
                    motionConfig: "gentle",
                };
                return buildPieOption(
                    period.temporalLabel,
                    base,
                    comparison,
                    chartData
                );
            });
        }

        return keysToUse.map((key) => {
            const cmp = comparisons.find((c) => c.key === key) || {};
            const chartData = getChartData(data, key);

            // ORIGINAL base object — unchanged
            const base = {
                margin: { top: 75, right: 75, bottom: 75, left: 70 },
                innerRadius: 0.75,
                padAngle: 2.5,
                cornerRadius: 3,
                borderWidth: 1,
                borderColor: cmp.borderColor,
                enableArcLabels: false,
                isInteractive: true,
                tooltip: createDashboardTooltip(
                    displayLabelMap[key]
                        ? displayLabelMap[key](organizationName)
                        : cmp.name || key
                ),

                // extracted (no logic change)
                arcLinkLabel: dashboardArcLinkLabelFn,

                enableArcLinkLabels: true,
                arcLinkLabelsSkipAngle: 0,
                arcLinkLabelsTextColor: "#525252",
                arcLinkLabelsThickness: 1,
                arcLinkLabelsColor: "#015294",
                arcLinkLabelsOffset: 0,
                arcLinkLabelsDiagonalLength: 15,
                arcLinkLabelsStraightLength: 0,
                animate: true,
                motionConfig: "gentle",
            };

            return buildPieOption(key, base, cmp, chartData);
        });
    }, [
        data,
        keysToUse,
        comparisons,
        buildPieOption,
        isTemporalChart,
        organizationName,
    ]);

    // ------------------------
    // Disability Snapshot Options
    // ------------------------
    const disabilitySnapshotPieOptions = useMemo(() => {
        const symbolShape = createSymbolShapeFn(comparisons);

        return keysToUse.map((key) => {
            const cmp = comparisons.find((c) => c.key === key) || {};
            const chartData = getChartData(data, key);

            // ORIGINAL base object — unchanged
            const base = {
                margin: { top: 73, right: 210, bottom: 113, left: 14 },
                innerRadius: 0.75,
                padAngle: 2.5,
                cornerRadius: 3,
                startAngle: -45,
                borderWidth: 2,
                borderColor: cmp.borderColor || "#015294",
                enableArcLabels: false,
                isInteractive: true,
                // extracted (no logic change)
                tooltip: disabilityTooltipFn,
                // extracted

                arcLinkLabel: disabilityArcLinkLabelFn,

                enableArcLinkLabels: true,
                arcLinkLabelsSkipAngle: 0,
                arcLinkLabelsTextColor: "#525252",
                arcLinkLabelsThickness: 1,
                arcLinkLabelsColor: "#015294",
                arcLinkLabelsOffset: 0,
                arcLinkLabelsDiagonalLength: 13,
                arcLinkLabelsStraightLength: 0,
                animate: true,
                motionConfig: "gentle",
                legends: [
                    {
                        anchor: "right",
                        direction: "column",
                        translateX: 108,
                        translateY: 0,
                        itemWidth: 95,
                        itemHeight: 34,
                        symbolSize: 15,

                        // extracted
                        symbolShape: symbolShape,
                    },
                ],
            };

            return buildPieOption(key, base, cmp, chartData);
        });
    }, [data, keysToUse, comparisons, buildPieOption]);

    const labels = useMemo(
        () =>
            isTemporalChart
                ? data.map((period) => period.temporalLabel)
                : keysToUse.map((key) =>
                      displayLabelMap[key]
                          ? displayLabelMap[key](organizationName)
                          : key
                  ),
        [data, isTemporalChart, keysToUse, organizationName]
    );

    const hasChartData = isTemporalChart
        ? data.some((period) =>
              period.values?.some(
                  (value) =>
                      typeof value.value === "number" &&
                      !Number.isNaN(value.value)
              )
          )
        : hasValidNumbers(data);

    if (
        isTemporalChart &&
        !disabilitySnapshot &&
        (chartId === "claimsByGender" ||
            chartId === "genderDistribution" ||
            chartId === "leavesByGender" ||
            title === "Claims by Gender" ||
            title === "Leaves by Gender")
    ) {
        return (
            <TemporalGenderChart
                title={title}
                description={description}
                data={data}
                groupBy={groupBy}
                layout={layout}
                organizationName={organizationName}
                clientClaimCount={clientClaimCount}
                errorMessage={errorMessage}
                selectedFilters={selectedFilters}
            />
        );
    }

    if (!hasChartData && disabilitySnapshot !== true) {
        return (
            <ChartCard
                title={title}
                description={description}
                accessibilityText={accessibilityText}
                variant="donut"
                groupBy={groupBy}
                chartOptions={[]}
                labels={[]}
                selectedCompanyYourCompanyTo={selectedCompanyYourCompanyTo}
                selectedFilters={selectedFilters}
                errorMessage={errorMessage}
                disabilitySnapshot={disabilitySnapshot}
                viewId={viewId}
                tableData={tableData}
                reportDetails={reportDetails}
                disclaimer={disclaimer}
                clientClaimCount={clientClaimCount}
            />
        );
    }

    if (isTemporalChart && showPeriodBars) {
        const categories = [
            ...new Set(
                data.flatMap((period) =>
                    (period.values || []).map((value) => value.gender)
                )
            ),
        ];
        const barData = categories.map((category) => {
            const row = { label: category };
            data.forEach((period) => {
                const value =
                    period.values?.find((item) => item.gender === category)
                        ?.value ?? 0;
                row[period.temporalLabel] = value;
                row[`${period.temporalLabel}Display`] =
                    formatPieDisplayValue(value);
            });
            return row;
        });
        return (
            <ReusableBarChart
                title={title}
                description={description}
                accessibilityText={accessibilityText}
                organizationName={organizationName}
                data={barData}
                indexBy="label"
                groupBy={groupBy}
                temporalLabels={data.map((period) => period.temporalLabel)}
                initialPeriodsOnAxis
                onToggleChartView={() => setShowPeriodBars(false)}
                layout="vertical"
                chartValueType="percentage"
                axisLabelOrientation="horizontal"
                chartValueOrientation="horizontal"
                selectedCompanyYourCompanyTo={[]}
                selectedFilters={selectedFilters}
                disabilitySnapshot={false}
                errorMessage={errorMessage}
                clientClaimCount={clientClaimCount}
            />
        );
    }

    return (
        <ChartCard
            title={title}
            description={description}
            accessibilityText={accessibilityText}
            variant="donut"
            groupBy={groupBy}
            onToggleGrouping={
                canToggleGrouping ? () => setShowPeriodBars(true) : undefined
            }
            chartOptions={
                disabilitySnapshot
                    ? disabilitySnapshotPieOptions
                    : dashboardPieOptions
            }
            percentageGroups={
                disabilitySnapshot
                    ? []
                    : dashboardPieOptions.map((option) =>
                          option.data.map((item) => item.value)
                      )
            }
            labels={labels}
            selectedCompanyYourCompanyTo={selectedCompanyYourCompanyTo}
            selectedFilters={selectedFilters}
            errorMessage={errorMessage}
            disabilitySnapshot={disabilitySnapshot}
            viewId={viewId}
            pieTotalRecords={totalRecords}
            tableData={tableData}
            reportDetails={reportDetails}
            disclaimer={disclaimer}
            cacheKey={cacheKey}
            clientClaimCount={clientClaimCount}
        />
    );
};

ReusableDonutChart.propTypes = {
    chartId: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    accessibilityText: PropTypes.string,
    organizationName: PropTypes.string,
    selectedCompanyYourCompanyTo: PropTypes.array,
    selectedFilters: PropTypes.object,
    groupBy: PropTypes.string,
    layout: PropTypes.oneOf(["horizontal", "vertical"]),
    data: PropTypes.array.isRequired,
    disabilitySnapshot: PropTypes.bool,
    errorMessage: PropTypes.string,
    viewId: PropTypes.string,
    totalRecords: PropTypes.number,
    disclaimer: PropTypes.string,
    clientClaimCount: PropTypes.number,
};

export default ReusableDonutChart;
